import React, { useCallback, useEffect, useRef, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});

const CAMERA_API = "/api/live-sources";

// 민트캠 카메라용 초록 마커
const cameraIcon = L.divIcon({
  className: "mintcam-marker",
  html:
    '<div style="background:#16a34a;width:16px;height:16px;border-radius:50%;border:2px solid #fff;box-shadow:0 0 0 1.5px #16a34a;"></div>',
  iconSize: [16, 16],
  iconAnchor: [8, 8],
  popupAnchor: [0, -8],
});

function getCameraLabel(cam) {
  const name = cam?.name || cam?.camName || cam?.deviceId || "민트캠";
  const battery =
    cam?.battery === null || cam?.battery === undefined ? "-" : `${cam.battery}%`;
  const status = String(cam?.liveStatus || "").toUpperCase() === "ONLINE" ? "정상" : "연결 안됨";
  const updatedAt = cam?.locationUpdatedAt || cam?.updatedAt || "-";
  const lat = Number(cam?.latitude);
  const lng = Number(cam?.longitude);
  const mapLink =
    Number.isFinite(lat) && Number.isFinite(lng)
      ? `<a href="https://www.google.com/maps?q=${lat},${lng}" target="_blank" rel="noreferrer" style="color:#2563eb;">구글 지도에서 열기</a>`
      : "";
  return `
    <div style="min-width:180px;">
      <div style="font-weight:700; margin-bottom:6px;">📷 ${name} (민트캠)</div>
      <div>상태: ${status}</div>
      <div>배터리: ${battery}</div>
      <div style="margin-top:6px; color:#666;">위치 갱신: ${updatedAt}</div>
      <div style="margin-top:6px;">${mapLink}</div>
    </div>
  `;
}

function toNumber(value) {
  if (value == null || value === "") return NaN;
  const n = Number(value);
  return Number.isFinite(n) ? n : NaN;
}

function pickLatLng(item) {
  const latCandidates = [
    item?.GpsLat,
    item?.gpsLat,
    item?.lat,
    item?.Lat,
    item?.latitude,
    item?.Latitude,
    item?.gps?.lat,
    item?.gps?.latitude,
    item?.location?.lat,
    item?.location?.latitude,
    item?.y,
  ];

  const lngCandidates = [
    item?.GpsLng,
    item?.gpsLng,
    item?.lng,
    item?.Lng,
    item?.lon,
    item?.Lon,
    item?.longitude,
    item?.Longitude,
    item?.gps?.lng,
    item?.gps?.lon,
    item?.gps?.longitude,
    item?.location?.lng,
    item?.location?.lon,
    item?.location?.longitude,
    item?.x,
  ];

  let nlat = NaN;
  let nlng = NaN;

  for (const v of latCandidates) {
    nlat = toNumber(v);
    if (Number.isFinite(nlat)) break;
  }

  for (const v of lngCandidates) {
    nlng = toNumber(v);
    if (Number.isFinite(nlng)) break;
  }

  if (!Number.isFinite(nlat) || !Number.isFinite(nlng)) return null;
  if (Math.abs(nlat) > 90 || Math.abs(nlng) > 180) return null;
  if (nlat === 0 && nlng === 0) return null;

  return [nlat, nlng];
}

// (여기 있던 toMillis·isFresh·getItemId·getItemLabel 은 민트워치 마커 전용이라 2026-10-07 제거)

// (여기 있던 positionsChanged 는 좌표가 1cm만 흔들려도 «전체보기»를 다시 걸어
//  확대한 화면을 2초마다 되돌려놨다. 2026-10-07 제거 — 이제 카메라 «목록»이 바뀔 때만 맞춘다.)

// 지도는 민트캠 GPS만 그린다.
//   민트워치(파란 마커)는 2026-10-07 제거 — 삼우에 민트워치를 넣지 않기로 했다.
export default function GpsMapWrapper() {
  const mapDivRef = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef(new Map());

  // 자동 «전체보기» 제어.
  //   didFitRef      : 최초 1회는 맞춰준다
  //   userMovedRef   : 사용자가 확대/이동했으면 그 뒤로는 절대 건드리지 않는다
  //   programmaticRef: 우리가 움직인 것인지(= 사용자 조작 아님) 구분용
  //   lastIdKeyRef   : 카메라 목록이 실제로 바뀌었는지 비교용(좌표 흔들림은 무시)
  const didFitRef = useRef(false);
  const userMovedRef = useRef(false);
  const programmaticRef = useRef(false);
  const lastIdKeyRef = useRef("");
  const lastItemsRef = useRef([]);
  const [userMoved, setUserMoved] = useState(false);

  useEffect(() => {
    if (mapRef.current) return;

    const center = [37.5665, 126.978];

    const map = L.map(mapDivRef.current, {
      zoomControl: true,
    }).setView(center, 13);

    mapRef.current = map;

    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: "&copy; OpenStreetMap contributors",
      maxZoom: 19,
    }).addTo(map);

    // 사용자가 직접 확대·이동한 순간부터 자동 맞춤을 멈춘다.
    const onMoveStart = () => {
      if (programmaticRef.current) return;
      userMovedRef.current = true;
      setUserMoved(true);
    };
    const onMoveEnd = () => {
      programmaticRef.current = false;
    };

    map.on("dragstart", onMoveStart);
    map.on("zoomstart", onMoveStart);
    map.on("moveend", onMoveEnd);
    map.on("zoomend", onMoveEnd);

    const invalidate = () => {
      mapRef.current?.invalidateSize();
    };

    setTimeout(invalidate, 0);
    window.addEventListener("resize", invalidate);

    return () => {
      window.removeEventListener("resize", invalidate);
      map.off("dragstart", onMoveStart);
      map.off("zoomstart", onMoveStart);
      map.off("moveend", onMoveEnd);
      map.off("zoomend", onMoveEnd);
      map.remove();
      mapRef.current = null;
    };
  }, []);

  // 마커 전체가 보이도록 맞춘다(자동/수동 공용).
  const fitToItems = useCallback((items) => {
    const map = mapRef.current;
    if (!map || !items || items.length === 0) return;

    programmaticRef.current = true;

    if (items.length === 1) {
      map.setView(items[0].pos, 18, { animate: true });
      return;
    }

    map.fitBounds(L.latLngBounds(items.map((i) => i.pos)).pad(0.25), {
      animate: true,
    });
  }, []);

  // «전체 보기» 버튼: 자동 맞춤을 다시 켜고 지금 바로 맞춘다.
  const handleFitAll = useCallback(() => {
    userMovedRef.current = false;
    setUserMoved(false);
    fitToItems(lastItemsRef.current);
  }, [fitToItems]);

  useEffect(() => {
    let timer;

    const fetchAndRender = async () => {
      try {
        // 민트캠 GPS만 가져온다.
        //   예전에는 민트워치 위치(/api/all-watch-data)도 함께 불러 파란 마커로 찍었는데,
        //   삼우에 민트워치를 넣지 않기로 해서 2026-10-07 제거했다.
        const camRes = await fetch(CAMERA_API, {
          cache: "no-store",
          credentials: "include",
        }).catch(() => null);

        const items = [];

        // 민트캠 카메라 마커 (초록). 위/경도가 있는 카메라만.
        const camRaw = camRes ? await camRes.json().catch(() => null) : null;
        const camArr = Array.isArray(camRaw?.sources)
          ? camRaw.sources
          : Array.isArray(camRaw)
          ? camRaw
          : [];

        for (const cam of camArr) {
          // 온라인 디바이스만 지도에 표시 (오프라인 카메라의 마지막 GPS 흔적 제거)
          if (String(cam?.liveStatus || "").toUpperCase() !== "ONLINE") continue;
          const pos = pickLatLng(cam);
          if (!pos) continue;
          items.push({
            id: `cam-${cam?.camIdx ?? cam?.id ?? cam?.streamKey ?? pos.join(",")}`,
            pos,
            label: getCameraLabel(cam),
            isCamera: true,
          });
        }

        const seen = new Set();

        items.forEach(({ id, pos, label, isCamera }) => {
          seen.add(id);

          const existing = markersRef.current.get(id);
          if (existing) {
            existing.setLatLng(pos);
            existing.setPopupContent(label);
          } else if (mapRef.current) {
            const marker = L.marker(pos, isCamera ? { icon: cameraIcon } : undefined)
              .addTo(mapRef.current)
              .bindPopup(label);
            markersRef.current.set(id, marker);
          }
        });

        for (const [id, marker] of markersRef.current.entries()) {
          if (!seen.has(id) && mapRef.current) {
            mapRef.current.removeLayer(marker);
            markersRef.current.delete(id);
          }
        }

        lastItemsRef.current = items;

        // 자동 «전체보기»는 두 경우만.
        //   ① 처음 마커가 떴을 때 ② 카메라가 새로 붙거나 빠졌을 때(사용자가 손대기 전까지만)
        //   좌표가 흔들리는 것만으로는 절대 다시 맞추지 않는다 — 확대한 화면이 되돌려지기 때문.
        const idKey = items
          .map((i) => i.id)
          .sort()
          .join("|");
        const listChanged = idKey !== lastIdKeyRef.current;
        lastIdKeyRef.current = idKey;

        if (mapRef.current && items.length > 0) {
          if (!didFitRef.current) {
            fitToItems(items);
            didFitRef.current = true;
          } else if (listChanged && !userMovedRef.current) {
            fitToItems(items);
          }
        }

        mapRef.current?.invalidateSize();
      } catch (err) {
        console.error("❌ 지도 데이터 실패:", err);
      }
    };

    fetchAndRender();
    timer = setInterval(fetchAndRender, 2000);

    return () => clearInterval(timer);
  }, [fitToItems]);

  return (
    <div className="relative h-full min-h-[260px] w-full overflow-hidden rounded-xl border border-gray-200 dark:border-gray-800 bg-gray-100 dark:bg-[#1F2A3B]">
      <div ref={mapDivRef} className="h-full w-full" />

      {/* 확대해서 보던 중 전체를 다시 보고 싶을 때. 사용자가 지도를 건드린 뒤에만 나온다. */}
      {userMoved && (
        <button
          type="button"
          onClick={handleFitAll}
          className="absolute right-3 top-3 z-[1000] rounded-lg border border-gray-300 bg-white/95 px-3 py-2 text-xs font-semibold text-gray-700 shadow-md hover:bg-white dark:border-gray-600 dark:bg-[#2A3850]/95 dark:text-gray-100 dark:hover:bg-[#2A3850]"
        >
          전체 보기
        </button>
      )}
    </div>
  );
}